import axios from 'axios';
import { bindAll, get } from 'lodash';

/* eslint-disable import/no-cycle */
import { history } from 'index';

import config from 'config/config';
import { Token } from 'models';

export default class Api {
  constructor() {
    bindAll(this, [
      'getPageViews',
      'getClicks',
      'getFormInteractions',
      'getSessions',
    ]);
  }

  extractEndpointFromLink(url) {
    if (!url) return null;
    return url.replace(config.apiDomain, '');
  }

  getCancelSource() {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    return source;
  }

  request({
    url,
    body,
    method = 'get', // get, head, post, put, patch, delete OR options
    withCancel,
    cb,
    cbError,
  }) {
    const source = this.getCancelSource();

    const requestConfig = {
      url: config.apiDomain + url,
      method,
      cancelToken: source.token,
    };

    if (method === 'get') {
      requestConfig.params = body;
    } else {
      requestConfig.data = body;
    }

    const apiResponse = axios(this.configureAuth(requestConfig))
      .then(response => {
        if (cb) cb(response);
        return response;
      })
      .catch((error) => {
        const status = get(error, 'response.status');
        const links = get(config, `serverResponseStatus.${status}`);

        if (axios.isCancel(error)) return false;
        if (cbError) cb(cbError);
        if (links) this.redirect(links);

        return Promise.reject(error.response);
      });

    if (withCancel) return { response: apiResponse, cancelToken: source };
    return apiResponse;
  }

  redirect(links) {
    links = (typeof (links) === 'string') ? [links] : links;
    if (links.every(link => link !== window.location.pathname)) {
      history.replace(links[0]);
    }
  }

  getSegmentTypes() {
    return this.request({ url: '/segment_types' });
  }

  getSegments() {
    return this.request({ url: '/segments' });
  }

  configureAuth(requestConfig) {
    const token = Token.get();

    if (!requestConfig.headers) requestConfig.headers = {};

    requestConfig.headers = {
      ...requestConfig.headers,
      'Access-Control-Expose-Headers': 'location',
      'Content-Type': 'application/json',
      Authorization: token ? `bearer ${token}` : '',
    };

    return requestConfig;
  }

  getAccount() {
    return this.request({ url: '/v1/account' });
  }

  getTags() {
    return this.request({ url: '/tags' });
  }

  removeUsersTag({
    tag_name,
    usersId,
    cb,
    cbError,
  }) {
    this.createTag({ tag_name, usersId, cb, cbError, untag: true });
  }

  createTag({
    tag_name,
    usersId,
    untag = false,
    cb,
    cbError,
  }) {
    const body = { name: tag_name };

    if (usersId && usersId.length) {
      const users = usersId.map(user_id => ({ user_id, untag }));
      body.users = users;
    }

    return this.request({
      url: '/tags',
      method: 'post',
      body,
      cb,
      cbError,
    });
  }

  getUserData({ user_id, cb, cbError }) {
    const limit = 1;
    return this.getUsers({ user_id, limit, cb, cbError });
  }

  getUsers({
    limit = 90,
    user_id,
    tag_id,
    segment_id,
    cb,
    cbError,
    is_identified, // True, False
  }) {
    let url = '/users';
    const linkParts = [];

    if (limit) linkParts.push(`limit=${limit}`);
    if (tag_id) linkParts.push(`tag_id=${tag_id}`);
    if (segment_id) linkParts.push(`segment_id=${segment_id}`);
    if (user_id) linkParts.push(`user_id=${user_id}`);
    if (is_identified) linkParts.push(`is_identified=${is_identified}`);
    if (linkParts.length) url = `${url}?${linkParts.join('&')}`;

    return this.request({ url, cb, cbError });
  }

  getClicks({
    user_id,
    limit,
    sort,
  }) {
    const url = this.generateDetailsUrl({
      url: '/clicks',
      user_id,
      limit,
    });
    return this.request({ url });
  }

  getFormInteractions({
    user_id,
    limit,
    sort,
  }) {
    const url = this.generateDetailsUrl({
      url: '/form_interactions',
      user_id,
      limit,
    });
    return this.request({ url });
  }

  getPageViews({
    user_id,
    limit,
    sort,
  }) {
    const url = this.generateDetailsUrl({
      url: '/pageviews',
      user_id,
      limit,
      sort,
    });
    return this.request({ url });
  }

  getSessions({
    user_id,
    limit,
    sort,
  }) {
    const url = this.generateDetailsUrl({
      url: '/sessions',
      user_id,
      limit,
      sort,
    });
    return this.request({ url });
  }

  generateDetailsUrl({
    url,
    user_id,
    limit,
    sort,
  }) {
    const params = [];
    if (limit) params.push(`limit=${limit}`);
    if (sort) params.push(`order=${sort}`);
    if (user_id) params.push(`user_id=${user_id}`);
    if (params.length) url += `?${params.join('&')}`;
    return url;
  }
}



// WEBPACK FOOTER //
// ./src/api/Api.js